import React from "react";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Grid,
  Card,
  IconButton,
  Chip,
  Button,
  LinearProgress,
  Pagination,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect,useRef } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import Search from "@mui/icons-material/Search";
import Visibility from "@mui/icons-material/Visibility";
import MonthlyTarget from "./monthlyTarget";
import { getAllPreSeasonActivitiesAction, getAllSeasonsAction, getAllDistrictsAction, getAllMandalsAction, getAllMonthlyTargetsAction } from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

function SOorMDOPreActivity() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isIpad = useMediaQuery(theme.breakpoints.only("sm"));
  const [query, setQuery] = React.useState("");
  const [openMonthlyTarget, setOpenMonthlyTarget] = React.useState(false);
  const [selectedPreTargetId, setSelectedPreTargetId] = React.useState(null);
  const [submittedPreTargetIds, setSubmittedPreTargetIds] = React.useState(new Set());
  const [page, setPage] = React.useState(1);
  const PAGE_SIZE = 30;

  // Get monthly targets to know which pre-targets already have monthly data
  const { successData: monthlyTargetsData } = useSelector((state) => state.getAllMonthlyTargetsReducer || {});
  const monthlyTargetPreIds = React.useMemo(() => {
    const list = Array.isArray(monthlyTargetsData?.data) ? monthlyTargetsData.data : [];
    return new Set(
      list
        .map((m) => m.pre_target_id ?? m.preTargetId ?? m.preTarget?.id)
        .filter((id) => id !== undefined && id !== null)
        .map((id) => String(id))
    );
  }, [monthlyTargetsData]);

  // Get pre-season activities data from Redux store
  const { successData: preSeasonActivitiesData } = useSelector(
    (state) => state.getAllPreSeasonActivitiesReducer || {}
  );

  // Get districts and mandals from Redux
  const { successData: districtsData } = useSelector((state) => state.getAllDistrictsReducer || {});
  const { successData: mandalsData } = useSelector((state) => state.getAllMandalsReducer || {});
  const districtList = districtsData?.data?.districts || [];
  const mandalList = mandalsData?.data?.mandals || [];

  // Get seasons data from Redux store
  const { successData: seasonsData } = useSelector(
    (state) => state.getAllSeasonsReducer || {}
  );
  
  const fetched = useRef(false);
  // Fetch pre-season activities and seasons on component mount
   useEffect(() => {
    if (fetched.current) return;
   fetched.current = true;
    dispatch(getAllPreSeasonActivitiesAction());
    dispatch(getAllDistrictsAction());
    dispatch(getAllMandalsAction());
    dispatch(getAllSeasonsAction());
    dispatch(getAllMonthlyTargetsAction());
  }, [dispatch]);

  // Build a map of seasonId to {name, year} from real API data
  const seasonsMap = React.useMemo(() => {
    const map = {};
    const arr = seasonsData?.data?.seasons || [];
    arr.forEach(s => {
      map[s.seasonId || s.id] = s;
    });
    return map;
  }, [seasonsData]);

  // Progress bar color based on percentage
  const getProgressBarColor = (percentage) => {
    if (percentage >= 100) return '#10B981'; // Green for 100%+
    if (percentage >= 75) return '#3B82F6'; // Blue for 75-99%
    if (percentage >= 50) return '#F59E0B'; // Orange for 50-74%
    return '#EF4444'; // Red for <50%
  };

  // Normalization helper for SO/MDO response formats
  const normalizePreSeasonActivities = React.useCallback((rawData) => {
    if (!Array.isArray(rawData) || rawData.length === 0) return [];
    const flatRows = [];
    const seenIds = new Set();

    rawData.forEach((entry) => {
      // Flatten all district-level targets
      (entry.targets || []).forEach((target) => {
        const {
          id,
          season_id,
          target_area_type,
          target_area_id,
          vlm_count,
          vlm_done_count,
          mvlm_count,
          mvlm_done_count,
          farmers_required,
          farmers_attended,
          is_active,
          season,
          details
        } = target || {};
        if (id && !seenIds.has(id)) {
          flatRows.push({
            id,
            season_id,
            target_area_type,
            target_area_id,
            vlm_count,
            vlm_done_count,
            mvlm_count,
            mvlm_done_count,
            farmers_required,
            farmers_attended,
            is_active,
            season,
            details: Array.isArray(details) ? details : [],
          });
          seenIds.add(id);
        }
        // Flatten all mandal-level pre_targets
        if (target.mandals && Array.isArray(target.mandals)) {
          target.mandals.forEach((mandal) => {
            (mandal.pre_targets || []).forEach((preTarget) => {
              const {
                id,
                season_id,
                target_area_type,
                target_area_id,
                vlm_count,
                vlm_done_count,
                mvlm_count,
                mvlm_done_count,
                farmers_required,
                farmers_attended,
                is_active,
                season,
                details
              } = preTarget || {};
              if (id && !seenIds.has(id)) {
                flatRows.push({
                  id,
                  season_id,
                  target_area_type,
                  target_area_id,
                  vlm_count,
                  vlm_done_count,
                  mvlm_count,
                  mvlm_done_count,
                  farmers_required,
                  farmers_attended,
                  is_active,
                  season,
                  details: Array.isArray(details) ? details : [],
                });
                seenIds.add(id);
              }
            });
          });
        }
      });
    });
    return flatRows;
  }, []);

  // Use normalized data
  const data = React.useMemo(() => normalizePreSeasonActivities(preSeasonActivitiesData?.data), [preSeasonActivitiesData, normalizePreSeasonActivities]);

  const filteredData = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data;
    return data.filter((item) => 
      [item.id, item.season_id, item.target_area_type, item.target_area_id, item.vlm_count, item.mvlm_count].some((v) => 
        String(v).toLowerCase().includes(q)
      )
    );
  }, [query, data]);

  // Reset to page 1 when search changes
  React.useEffect(() => { setPage(1); }, [query]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));
  const pagedData = filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleViewDetails = (data) => {
    navigate("/SOorMDO/viewPreActivity", { state: { preActivityData: data } });
  };

  const handleCloseModal = () => {
    setOpenMonthlyTarget(false);
    setSelectedPreTargetId(null);
  };

  const handleAddNew = (id) => {
    setSelectedPreTargetId(id ?? null);
    setOpenMonthlyTarget(true);
  };

 
  

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 0}}>
        Pre Activity
      </Typography>

      <Card sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item size={{ xs: 12, sm: 8, md: 6 }}>
            <TextField
              size="small"
              placeholder="Search ..."
              slotProps={{
                input: {
                  startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
                },
              }}
              fullWidth
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 4, md: 6 }} sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" }, gap: 2 }}>
            {/* <Button 
              variant="contained" 
              color="primary" 
              sx={{ borderRadius: 2 }} 
              onClick={() => navigate("/SOorMDO/createActivity")}
            >
              Add Activity
            </Button> */}
            {/* <Button variant="contained" color="primary" sx={{ borderRadius: 2 }} onClick={handleAddNew}>
              Update Monthly Target
            </Button> */}
          </Grid>
        </Grid>
      </Card>

 <Box sx={{  mb: 2 }}>
        {/* Header: visible only on sm and up, styled like Crop */}
        <Box
          display={{ xs: 'none', sm: 'grid' }}
          gridTemplateColumns={{ sm: '1fr 1fr 1fr 1fr 1fr 1fr 0.7fr 0.5fr' }}
          px={{ xs: 1, sm: 2 }}
          mb={{ xs: 1, sm: 1 }}
          sx={{ width: isIpad ? '97%' : undefined, mx: 'auto' }}
        >
          <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>SEASON</Typography>
          <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>AREA TYPE</Typography>
          <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>AREA ID</Typography>
          <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>VLM</Typography>
          <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>MVLM</Typography>
          <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>FARMER TARGET</Typography>
          <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>STATUS</Typography>
          <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>ACTIONS</Typography>
        </Box>

        {/* Rows styled like Crop */}
        {Array.isArray(filteredData) && filteredData.length > 0 ? (
          pagedData.map((activity) => (
            <Paper
              key={activity.id}
              elevation={0}
              sx={{
                mb: { xs: 1, sm: 2 },
                p: { xs: 1, sm: 2 },
                borderRadius: 3,
                display: { xs: 'block', sm: 'grid' },
                gridTemplateColumns: { sm: '1fr 1fr 1fr 1fr 1fr 1fr 0.7fr 0.5fr' },
                alignItems: 'center',
                border: '1px solid #E5E7EB',
                gap: { xs: 0, sm: 0 },
                width: isIpad ? '97%' : undefined,
                mx: 'auto',
                cursor: 'pointer',
                '&:hover': {
                  border: '2px solid #2563EB',
                  boxShadow: '0 4px 16px 0 rgba(37,99,235,0.15)',
                },
              }}
            >
              {/* Mobile layout */}
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                <Box mb={1} display="flex" alignItems="center" >
                  <Typography fontWeight={600} minWidth={100}>Season:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Typography variant="body2">{activity.season?.name || (seasonsMap[activity.season_id]?.name)} {activity.season?.year || (seasonsMap[activity.season_id]?.year)}</Typography>
                    <Chip
                      label={activity.is_active ? "Active" : "Inactive"}
                      size="small"
                      sx={{ width: 80, backgroundColor: activity.is_active ? '#10B981' : '#F87171', color: '#fff', ml: 'auto' }}
                    />
                  </Box>
                </Box>
                <Box mb={1} display="flex" alignItems="center" gap={0}>
                  <Typography fontWeight={600} minWidth={100}>Area Type:</Typography>
                  <Typography variant="body2">{activity.target_area_type}</Typography>
                </Box>
                <Box mb={1} display="flex" alignItems="center" gap={0}>
                  <Typography fontWeight={600} minWidth={100}>Area Name:</Typography>
                  <Typography variant="body2">
                    {activity.target_area_type === 'mandal' ? (() => {
                      const mandal = mandalList.find(m => m.mandalId === activity.target_area_id || m.id === activity.target_area_id);
                      const district = districtList.find(d => d.districtId === mandal?.districtId || d.id === mandal?.districtId);
                      return district && mandal ? `${district.name} - ${mandal.name}` : mandal ? mandal.name : activity.target_area_id;
                    })() : activity.target_area_type === 'district' ? (() => {
                      const district = districtList.find(d => d.districtId === activity.target_area_id || d.id === activity.target_area_id);
                      return district ? district.name : activity.target_area_id;
                    })() : activity.target_area_id}
                  </Typography>
                </Box>
                <Box mb={1} display="flex" alignItems="center" gap={0}>
                  <Typography fontWeight={600} minWidth={100}>VLM:</Typography>
                  <Box>
                    <Typography variant="body2">{activity.vlm_done_count}/{activity.vlm_count}</Typography>
                  </Box>
                </Box>
                <Box mb={1} display="flex" alignItems="center" gap={0}>
                  <Typography fontWeight={600} minWidth={100}>MVLM:</Typography>
                  <Box>
                    <Typography variant="body2">{activity.mvlm_done_count}/{activity.mvlm_count}</Typography>
                  </Box>
                </Box>
                <Box mb={1} display="flex" alignItems="center" gap={0}>
                  <Typography fontWeight={600} minWidth={100}>FARMER TARGET:</Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>{activity.farmers_attended || 0}/{activity.farmers_required || 0}</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={activity.farmers_required > 0 ? Math.min((activity.farmers_attended || 0) / activity.farmers_required * 100, 100) : 0}
                      sx={{
                        height: 8,
                        borderRadius: 1,
                        backgroundColor: '#E5E7EB',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: getProgressBarColor(activity.farmers_required > 0 ? (activity.farmers_attended || 0) / activity.farmers_required * 100 : 0),
                          borderRadius: 1,
                        },
                      }}
                    />
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={0}>
                  <Typography fontSize={11} color="#000000" minWidth={100}></Typography>
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                    {!monthlyTargetPreIds.has(String(activity.id)) && !submittedPreTargetIds.has(activity.id) ? (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ borderRadius: 2, backgroundColor: '#4F46E5', color: '#fff', fontSize: 12 }}
                        onClick={() => handleAddNew(activity.id)}
                      >
                        Update Monthly Target
                      </Button>
                    ) : (
                      <IconButton size="small" onClick={() => handleViewDetails(activity)} sx={{ border: 'none' }}>
                        <Visibility fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Box>
              {/* Desktop layout */}
              {/* Desktop columns: 9 columns, status then actions */}
              <Typography display={{ xs: 'none', sm: 'block' }} variant="body2">{activity.season?.name || (seasonsMap[activity.season_id]?.name)} {activity.season?.year || (seasonsMap[activity.season_id]?.year)}</Typography>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2">{activity.target_area_type}</Typography>
              </Box>
              <Typography display={{ xs: 'none', sm: 'block' }} variant="body2">
                {activity.target_area_type === 'mandal' ? (() => {
                  const mandal = mandalList.find(m => m.mandalId === activity.target_area_id || m.id === activity.target_area_id);
                  const district = districtList.find(d => d.districtId === mandal?.districtId || d.id === mandal?.districtId);
                  return district && mandal ? `${district.name} - ${mandal.name}` : mandal ? mandal.name : activity.target_area_id;
                })() : activity.target_area_type === 'district' ? (() => {
                  const district = districtList.find(d => d.districtId === activity.target_area_id || d.id === activity.target_area_id);
                  return district ? district.name : activity.target_area_id;
                })() : activity.target_area_id}
              </Typography>
              <Box display={{ xs: 'none', sm: 'flex' }} flexDirection="column" gap={0.5} sx={{ ml: { lg: 0 } }}>
                <Typography variant="body2">{activity.vlm_done_count}/{activity.vlm_count}</Typography>
              </Box>
              <Box display={{ xs: 'none', sm: 'flex' }} flexDirection="column" gap={0.5} sx={{ ml: { lg: 0 } }}>
                <Typography variant="body2">{activity.mvlm_done_count}/{activity.mvlm_count}</Typography>
              </Box>
              <Box display={{ xs: 'none', sm: 'flex' }} flexDirection="column" gap={0.5} sx={{ ml: { lg: 0 }, pr: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>{activity.farmers_attended || 0}/{activity.farmers_required || 0}</Typography>
                <LinearProgress
                  variant="determinate"
                  value={activity.farmers_required > 0 ? Math.min((activity.farmers_attended || 0) / activity.farmers_required * 100, 100) : 0}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    backgroundColor: '#E5E7EB',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getProgressBarColor(activity.farmers_required > 0 ? (activity.farmers_attended || 0) / activity.farmers_required * 100 : 0),
                      borderRadius: 1,
                    },
                  }}
                />
              </Box>
              {/* Status column */}
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center', alignItems: 'center' }}>
                <Chip
                  label={activity.is_active ? "Active" : "Inactive"}
                  size="small"
                  sx={{ width: 80, backgroundColor: activity.is_active ? '#D1FAE5' : '#FECACA', color: activity.is_active ? '#047857' : '#B91C1C', fontSize: 12, fontWeight: 600 }}
                />
              </Box>
              {/* Actions column */}
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center', alignItems: 'center' }}>
                {!monthlyTargetPreIds.has(String(activity.id)) && !submittedPreTargetIds.has(activity.id) ? (
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ borderRadius: 2, backgroundColor: '#4F46E5', color: '#fff', fontSize: 12 }}
                    onClick={() => handleAddNew(activity.id)}
                  >
                    Update Monthly Target
                  </Button>
                ) : (
                  <IconButton size="small" onClick={() => handleViewDetails(activity)} sx={{ p: 0.5, border: 'none', background: '#F0FDF4', '&:hover': { background: '#D1FAE5' } }}>
                    <Visibility fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.15))' }} />
                  </IconButton>
                )}
              </Box>
            </Paper>
          ))
        ) : (
          <Typography align="center" color="text.secondary" sx={{ mt: 2 }}>
            No pre-season targets found.
          </Typography>
        )}
        {filteredData.length > 0 && totalPages > 1 && (
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={1}>
            <Typography variant="body2" color="text.secondary">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredData.length)} to {Math.min(page * PAGE_SIZE, filteredData.length)} of {filteredData.length} entries
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, val) => setPage(val)}
              color="primary"
            />
          </Box>
        )}
      </Box>
      
      <MonthlyTarget
        open={openMonthlyTarget}
        onClose={handleCloseModal}
        preTargetId={selectedPreTargetId}
        onSubmit={(payload) => {
          // Mark this pre-target as submitted so button switches to view icon immediately
          if (selectedPreTargetId) {
            setSubmittedPreTargetIds((prev) => new Set([...prev, selectedPreTargetId]));
          }
          // Refresh the list after successful submission
          dispatch(getAllPreSeasonActivitiesAction());
          dispatch(getAllMonthlyTargetsAction());
        }}
      />
    </Box>
   
  );
}

export default SOorMDOPreActivity;